'use client';

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from '@/hooks/use-wallet';
import { useEthPrice } from '@/hooks/use-eth-price';
import { useTokenPricesInteractive, tokenToUsd, effectiveTokenAddress } from '@/hooks/use-token-prices';
import { useUserProfile } from '@/hooks';
import { RateLimitError } from '@/lib/rate-limit-error';
import type { SeatData } from '@/components/grid';
import { grossRewardsPerTile, resolveRewardTokenInfo, type RewardResult } from '@/lib/rewards';
import type {
  V2GridPageData,
  V2Grid,
  V2GridSeat,
  V2GridAsset,
  V2FloorPriceSnapshot,
  V2GridSeatEvent,
  V2GridSeatEventType,
  V2GridRewardEvent,
  V2TopHolder,
  GridAdapterNote,
} from '@/lib/types/v2';
import type { V2MigrationContext } from '@/lib/api/fetchers/migration';
import { V2_CONSTANTS } from '@/lib/types/v2';
import { getHighResAvatarUrl, gridIdToPath } from '@/lib/utils';
import { isBoardManagerAddress } from '@/lib/admin/allow-list';
import { getTaxTokenConfig } from '@/lib/drawer-utils';
import { getBoardroomLiveGridId } from '@/features/lobby/data/boardroom-grid';
import { TAKEOVER_TOKEN_ADDRESS, isV22GridVersion } from '@/lib/constants/addresses';
import { useLiveGrid, type LiveSeat } from '@/hooks/use-live-grid';
import { useGridHistory } from '@/hooks/use-grid-history';
import { safeBigInt } from '@/lib/bigint';
import {
  WETH_ADDRESS,
  KNOWN_YIELD_SYMBOLS,
  KNOWN_YIELD_DECIMALS,
  BASE_SYMBOLS,
  createEmptySeat,
  mergeRecentGridEvents,
  buildForfeitTimeBySeat,
  buildForfeitOldHolderBySeat,
  buildSeatData,
  derivePriorityHolders,
  computeFloorAndTakeover,
  resolvePrimaryCoinAddress,
  resolveYieldTokenSymbol,
  synthesizeUnmaterializedSeats,
  parseInitialPricesPacked,
} from './use-grid-page.helpers';

export interface UseGridPageOptions {
  /**
   * When true, the hook also fetches the `/history` endpoint (floor-price
   * snapshots, reward events, daily stats) via `useGridHistory` and merges
   * those into the return shape so callers like the full grid detail page
   * keep working without extra wiring.
   *
   * Defaults to `false` — the lobby, coin pages and most other surfaces
   * only need the core snapshot and can opt in to history from the specific
   * panels that render charts / feeds.
   */
  includeHistory?: boolean;
}

interface UseGridPageReturn {
  address: string | null | undefined;
  avatarUrl: string | null | undefined;
  displayName: string | null | undefined;
  userLevel: number;
  grid: V2Grid | undefined;
  gridLoading: boolean;
  gridError: Error | null;
  seats: SeatData[];
  rawSeats: V2GridSeat[];
  assets: V2GridAsset[];
  floorPriceSnapshots: V2FloorPriceSnapshot[];
  recentEvents: V2GridSeatEvent[];
  rewardEvents: V2GridRewardEvent[];
  rewardEventsUsd: V2GridRewardEvent[];
  topHolders: V2TopHolder[];
  creatorIdentity: { address: string; displayName?: string; avatarUrl?: string; level?: number } | undefined;
  // Derived metrics
  floorPrice: number;
  floorPriceBigInt: bigint;
  takeoverCost: string;
  rawTakeoverCost: number;
  takeoverCostBigInt: bigint;
  isTakenOver: boolean;
  /**
   * True when the connected wallet holds every seat on the grid (`0 … totalSeats-1`),
   * each with a non-forfeited holder address matching the viewer. Used to gate
   * creator LP withdraw — stricter than {@link isTakenOver}, which only requires
   * every *occupied* seat to be ours (vacant seats excluded).
   */
  creatorOwnsAllTiles: boolean;
  isCreator: boolean;
  hasVacantSeats: boolean;
  isFreeMint: boolean;
  // Earnings metrics
  grossPerSeat: bigint;
  grossResult: RewardResult;
  is7dEstimate: boolean;
  poolAgeLabel: string | undefined;
  isEarningsUnreliable: boolean;
  totalDistributedUsd: bigint;
  totalDistributedRaw: number;
  ethPrice: number;
  floorPriceChange24h: number | null;
  priorityHolders: { address: string; heldSince: number; displayName?: string; avatarUrl?: string; level?: number }[];
  primaryCoinAddress: string | undefined;
  yieldTokenSymbol: string | undefined;
  yieldTokenAddress: string | undefined;
  yieldTokenDecimals: number;
  hasYieldPrice: boolean;
  yieldPriceUsd: number;
  totalTvlUsd: string | undefined;
  totalTvlNative: string | undefined;
  rawSocialLinks: V2GridPageData['socialLinks'] | undefined;
  /** `pool_metadata.description` when present */
  boardDescription: string | undefined;
  /**
   * Raw `pool_metadata.board_skybox` value as returned by the API:
   *   - `'none'` sentinel → creator picked None in the wizard
   *   - registered theme id (`'aurora'`, `'sci-fi'`, …) → use it
   *   - `undefined` → no choice yet (legacy / pre-wizard board)
   *
   * Surfaced here so direct `/board/<id>` access can apply the
   * creator's theming without waiting on `/api/v1/grids` to resolve
   * (the lobby-list mapper does the same conversion in
   * `gridSummaryToMockBoard`).
   */
  boardSkybox: string | undefined;
  /** Raw `pool_metadata.board_plinth` value — same contract as
   *  {@link UseGridPageReturn.boardSkybox}. */
  boardPlinth: string | undefined;
  adapterNotes: Record<string, GridAdapterNote> | undefined;
  taxTokenDecimals: number;
  /** Pending adapter yield in the same display format as rewardEventsUsd amounts */
  pendingYieldDisplay: number;
  /** TAKEOVER price in USD from the indexer (float, 0 if unavailable) */
  takeoverPriceUsdIndexer: number;
  /** CoinGecko / upstream warning when using interactive (fresh) yield pricing */
  yieldPriceWarning: string | undefined;
  /**
   * V1 → V2 migration context, or `null` when this grid isn't the
   * destination of an active migration. Stamped server-side from
   * classic's `pool_metadata` lookup (see
   * `app/api/grid/[ugm]/[gridId]/route.ts`). Drives the lobby's
   * `<V2MigrationBoardBanner>` and the post-claim "abandon V1"
   * nudge modal.
   */
  migration: V2MigrationContext | null;
}

export function useGridPage(
  gridId: string,
  options: UseGridPageOptions = {},
): UseGridPageReturn {
  const { includeHistory = false } = options;
  const queryClient = useQueryClient();
  const { address, linkedAddresses } = useWallet();

  // Stable key + Set for `buildSeatData`'s `viewerAddresses` arg.
  //
  // Privy emits a fresh `user` reference on every SDK tick, which churns
  // `useWallet().linkedAddresses` (memoised on `user`). Passing that Set
  // straight into the seats memo would rebuild ~100 `SeatData` rows on
  // every Privy heartbeat. Collapsing the membership to a deterministic
  // sorted-csv string lets React's referential dep check skip the
  // rebuild whenever the actual address set is unchanged.
  //
  // The Set is then derived FROM the key (not from `linkedAddresses`)
  // so its identity tracks the key, not the upstream Privy ref.
  const viewerAddressesKey = useMemo(() => {
    const lc = address?.toLowerCase();
    const merged = new Set<string>(linkedAddresses);
    if (lc) merged.add(lc);
    return [...merged].sort().join(',');
  }, [address, linkedAddresses]);
  const viewerAddresses = useMemo<ReadonlySet<string>>(
    () => new Set(viewerAddressesKey ? viewerAddressesKey.split(',') : []),
    [viewerAddressesKey],
  );

  const { ethPrice } = useEthPrice();
  const { avatarUrl, displayName, level: userLevel } = useUserProfile(address);

  // Magic id used by surfaces (e.g. lobby snapshot) that want the hook's shape
  // without firing any network calls. Skips the v2-grid REST poll, the SSE
  // event stream and the live seat poll.
  const isLiveDisabled = !gridId || gridId.startsWith('disabled-');

  const { data: pageData, isLoading: gridLoading, error: gridError } = useQuery({
    queryKey: ['v2-grid', gridId],
    enabled: !isLiveDisabled,
    queryFn: async () => {
      const res = await fetch(`/api/grid/${gridIdToPath(gridId)}`);
      if (res.status === 429) throw new RateLimitError();
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to fetch grid (${res.status})`);
      }
      const newData = await res.json();
      // Protect RPC-updated seats from being overwritten by stale indexer data.
      // After a transaction, refreshSeatFromRPC writes fresh on-chain data to this
      // cache key. If the indexer hasn't caught up yet, its response would have an
      // older lastPriceChangeTime — keep the cached (RPC) version for those seats.
      const cached = queryClient.getQueryData(['v2-grid', gridId]) as any;
      if (cached?.data?.seats && newData?.data?.seats) {
        const cachedMap = new Map(
          cached.data.seats.map((s: any) => [s.seatIndex, s])
        );
        newData.data.seats = newData.data.seats.map((s: any) => {
          const prev = cachedMap.get(s.seatIndex) as any;
          if (prev && (prev.lastPriceChangeTime ?? 0) > (s.lastPriceChangeTime ?? 0)) {
            return prev;
          }
          return s;
        });
      }
      return newData;
    },
    // 15s polling matches `main`. The on-chain `currentBalance` returned
    // by this endpoint is the only authoritative source for tax-decayed
    // runway; SSE gives us holder/price/active churn but NOT a live RPC
    // balance (Envio only stores `deposit`). 60s here meant the runway
    // anchor could lag the chain by up to ~75s — long enough to display
    // a healthy "59 minutes left" on a seat that had already decayed,
    // because the SSE merge wasn't (and shouldn't be) refreshing it.
    staleTime: 15_000,
    refetchInterval: 15_000,
  });

  const gridData = pageData?.data as V2GridPageData | undefined;
  const grid = gridData?.grid;
  const rawSeats = gridData?.seats ?? [];
  const assets = gridData?.assets ?? [];
  const apiRecentEvents = gridData?.recentEvents ?? [];

  // The core `/api/grid/[ugm]/[gridId]` response now carries only a 7-day
  // tail of `rewardEvents` (≤200 rows), which is all we need for the
  // first-paint `rewards7dRaw` / `grossResult` / `is7dEstimate` derivations
  // that feed the lobby + grid headers.
  //
  // Floor-price snapshots, full reward history and daily stats live in the
  // separate `/history` endpoint. `useGridHistory` is dormant unless
  // `includeHistory` is true; consumers that don't render charts (lobby
  // boot, coin pages, overview tab, etc.) avoid that network call entirely.
  const history = useGridHistory(gridId, includeHistory && !isLiveDisabled);
  const floorPriceSnapshots = history.floorPriceSnapshots;
  const coreRewardEvents = gridData?.rewardEvents ?? [];
  const rewardEvents = history.rewardEvents.length > 0 ? history.rewardEvents : coreRewardEvents;
  const topHolders = gridData?.topHolders ?? [];
  const creatorIdentity = gridData?.creatorIdentity;
  const rawSocialLinks = gridData?.socialLinks;
  const adapterNotes = gridData?.adapterNotes;
  const reservations = gridData?.reservations;
  const claimDeadline = gridData?.claimDeadline;
  const migration: V2MigrationContext | null = gridData?.migration ?? null;

  // Live grid SSE — background Envio polling + optimistic seat updates +
  // instant seat events. `events` here are the same events that used to be
  // delivered through a second `useLiveEvents` subscription to
  // `/api/live?poolId=X` — now multiplexed through the single `useLiveGrid`
  // connection so each board opens at most two streams (one `/api/live/grid`
  // + one `/api/live?poolId`) instead of three.
  const {
    seats: liveSeats,
    isLoaded: liveLoaded,
    events: liveEvents,
  } = useLiveGrid(gridId, !isLiveDisabled);

  const recentEvents: V2GridSeatEvent[] = useMemo(
    () => mergeRecentGridEvents(apiRecentEvents, liveEvents, gridId),
    [apiRecentEvents, liveEvents, gridId],
  );

  // Cascade refetch on real on-chain activity (CLAIMED / BOUGHT / ABANDONED /
  // FORFEITED / PRICE_UPDATED / DEPOSIT_ADDED / BALANCE_WITHDRAWN). The two
  // staggered timers (1s + 3s) match `main`: the first catches Envio when it
  // beats the cache, the second is a belt-and-braces in case the indexer
  // briefly lagged behind the chain.
  //
  // We deliberately do NOT cascade on the 3-second `gridPollManager` tick
  // (`liveUpdateCount`) — that path was the spammy one that made the heavy
  // grid endpoint run on every viewer's heartbeat. `liveEvents.length` only
  // grows on actual SSE-delivered transactions, so the worst case is a few
  // refetches around a buyout cluster, not a sustained hammering.
  const prevLiveCountRef = useRef(0);
  useEffect(() => {
    if (isLiveDisabled) return;
    if (liveEvents.length === 0 || liveEvents.length === prevLiveCountRef.current) return;
    prevLiveCountRef.current = liveEvents.length;
    const t1 = setTimeout(() => {
      queryClient.refetchQueries({ queryKey: ['v2-grid', gridId] });
    }, 1000);
    const t2 = setTimeout(() => {
      queryClient.refetchQueries({ queryKey: ['v2-grid', gridId] });
    }, 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [liveEvents.length, gridId, isLiveDisabled, queryClient]);

  // Yield denomination: rewards are stored in the grid's yieldToken units.
  // We look up the yieldToken's USD price via CoinGecko and use yieldTokenDecimals.
  const yieldAddr = effectiveTokenAddress(grid?.yieldToken);
  const yieldDecimals = KNOWN_YIELD_DECIMALS[yieldAddr] ?? grid?.yieldTokenDecimals ?? 18;
  const { prices: interactivePrices, warning: yieldPriceWarning } = useTokenPricesInteractive(
    yieldAddr ? [yieldAddr] : [],
  );
  const isEthLike = yieldAddr === WETH_ADDRESS;
  const yieldPriceUsd = isEthLike
    ? (ethPrice || interactivePrices[yieldAddr] || 0)
    : (interactivePrices[yieldAddr] || 0);

  const rawYieldToUsd = useCallback((raw: bigint): number => {
    return tokenToUsd(raw, yieldDecimals, yieldPriceUsd);
  }, [yieldDecimals, yieldPriceUsd]);

  // Tick counter that drives the per-second client-side recompute of
  // `seats`. `buildSeatData` reads `Date.now()` on every invocation and
  // the RPC-balance branch decays from `rpcCurrentBalanceAt`; bumping
  // this state forces React to re-run that derivation, which is how
  // the displayed runway visibly counts down between API refreshes.
  // Replaces the previous "soonest-runway < 5min" conditional timer
  // (`MAX_FORFEIT_TIMER_S`), which never fired for the 59-minute regime
  // the user reported. The tick suspends itself when no live seats are
  // held — there's nothing to decay on a vacant board.
  const [forfeitTick, setForfeitTick] = useState(0);

  const ANTI_SNIPE_WINDOW = V2_CONSTANTS.ANTI_SNIPE_WINDOW;

  // Merge live grid SSE seat data on top of API-provided rawSeats so the UI
  // reflects changes within seconds rather than waiting for the next API poll.
  const mergedRawSeats: V2GridSeat[] = useMemo(() => {
    if (!liveLoaded || liveSeats.length === 0) return rawSeats;

    const liveMap = new Map<number, LiveSeat>();
    for (const s of liveSeats) liveMap.set(s.seatIndex, s);

    const apiMap = new Map<number, V2GridSeat>();
    for (const s of rawSeats) apiMap.set(s.seatIndex, s);

    // Start from API seats, overlay with live data where available
    const merged: V2GridSeat[] = rawSeats.map((apiSeat) => {
      const live = liveMap.get(apiSeat.seatIndex);
      if (!live) return apiSeat;

      // Defensive normalisation. `LiveSeat.holder` is *typed* as
      // `string | null`, but the SSE payload arrives as JSON parsed
      // from a server that, depending on the backend path that
      // produced the row (Envio v2 vs an RPC fallback writing an
      // empty string for the zero-address sentinel — see
      // `app/api/grid/[ugm]/[gridId]/route.ts`), can deliver `''` or
      // an absent property (→ `undefined`). Anything that's not a
      // real address must collapse to `null` here so every
      // downstream consumer (`buildSeatData`'s `hasHolder`,
      // `tileTier`'s `!seat.holder`, the `differs` check below)
      // reads the same canonical "no holder" sentinel. Skipping
      // this normalisation surfaced as: a tile with a falsy-but-
      // not-strictly-null holder value would inherit
      // `output.holder = null` from `buildSeatData` AND
      // `output.isPendingForfeit = true` (because the legacy strict
      // `seat.holder !== null` check accepted `''` / `undefined`
      // as "has holder"), which the 2D renderer paints as
      // `<ShieldAlert>` + amber dashed border on tiles 3D treats
      // as plainly vacant.
      const liveHolder: `0x${string}` | null = live.holder
        ? (live.holder as `0x${string}`)
        : null;

      // Only overwrite when something materially changed: holder, price,
      // or active state. We deliberately do NOT compare `balance`: the
      // SSE `live.balance` is Envio's stored `GridSeat.deposit` (static —
      // only changes on actual deposit/withdraw events), NOT the on-chain
      // `currentBalance(gridId, seatId)` (which decays continuously with
      // tax). Including it in `differs` made every SSE poll trigger a
      // merge that overwrote `rpcCurrentBalance` with the static deposit,
      // freezing the displayed runway at the value it would have had if
      // no time had passed since the last on-chain action — the bug
      // users saw as "59 minutes left forever".
      //
      // Real deposit/withdraw events still surface here in two ways:
      //   1. They emit a CLAIMED/BOUGHT/DEPOSIT_ADDED/BALANCE_WITHDRAWN
      //      activity event that bumps `liveEvents.length`, which the
      //      cascade refetch effect above kicks the API for fresh
      //      `rpcCurrentBalance` (1s + 3s).
      //   2. The 15s React Query refetch interval picks them up too.
      // Both paths re-stamp `rpcCurrentBalance` from a real RPC multicall,
      // not from indexed `deposit`.
      if (
        liveHolder?.toLowerCase() === apiSeat.holder?.toLowerCase() &&
        live.selfAssessedPrice === apiSeat.selfAssessedPrice &&
        live.isActive === apiSeat.isActive
      ) {
        return apiSeat;
      }

      // Fresher API seat wins: `updateSeatsInCache` (in drawer-utils)
      // writes the RPC's `lastPriceChangeTime` back to the
      // `['v2-grid', gridId]` cache immediately after a successful
      // `setPrice` / `buyOut` / `deposit` call, and those writes are
      // strictly newer than anything the SSE stream has observed yet.
      // Without this guard the `mergedRawSeats` step would replay the
      // stale live-stream price on top of the fresh RPC write, so
      // OwnedTilesStrip / seat drawers would flash back to the old
      // price until the next SSE heartbeat caught up.
      const apiTime = apiSeat.lastPriceChangeTime ?? 0;
      const liveTime = live.lastUpdateTime ?? 0;
      if (apiTime > liveTime) return apiSeat;

      // We update `deposit` and `lastTaxTime` from the live SSE — those
      // are the indexed values, and the new holder genuinely starts with
      // a fresh deposit/lastTaxTime. We do NOT touch `rpcCurrentBalance`:
      // it stays as whatever the last API multicall returned (or
      // undefined for fresh seats). `buildSeatData` falls back to the
      // deposit-projection path when `rpcCurrentBalance` is missing for
      // a freshly-claimed seat, and the cascade refetch above will
      // populate it on the next poll.
      return {
        ...apiSeat,
        holder: liveHolder,
        selfAssessedPrice: live.selfAssessedPrice,
        deposit: live.balance,
        lastTaxTime: live.lastUpdateTime,
        isActive: live.isActive,
        // Clear `rpcCurrentBalance` AND `rpcCurrentBalanceAt` when the
        // holder changed: the previous holder's RPC balance applies to
        // a seat that no longer exists. Letting it persist would make
        // `buildSeatData` misreport the new owner's runway as 0 until
        // the next API poll. Falling back to the deposit-projection
        // path is correct here — the new holder's `deposit` and
        // `lastTaxTime` from the SSE are the authoritative anchor.
        ...(liveHolder?.toLowerCase() !== apiSeat.holder?.toLowerCase()
          ? { rpcCurrentBalance: undefined, rpcCurrentBalanceAt: undefined }
          : {}),
        // Clear identity when holder changed — will be refetched
        holderIdentity: liveHolder?.toLowerCase() === apiSeat.holder?.toLowerCase()
          ? apiSeat.holderIdentity
          : undefined,
      };
    });

    // Add any live seats that aren't in the API response yet. We do NOT
    // populate `rpcCurrentBalance` here — `live.balance` is the indexed
    // `GridSeat.deposit`, not the on-chain `currentBalance`, and stamping
    // it would make `buildSeatData` skip the deposit-projection path and
    // freeze the runway at the deposit value (the very bug Phase 1 fixes).
    // Fresh seats fall back to the deposit-projection path until the
    // cascade refetch (1s + 3s after the SSE event) populates a real
    // RPC balance.
    for (const [idx, live] of liveMap) {
      if (!apiMap.has(idx)) {
        const liveHolder: `0x${string}` | null = live.holder
          ? (live.holder as `0x${string}`)
          : null;
        merged.push({
          id: `${gridId}-${idx}`,
          gridId,
          seatIndex: idx,
          holder: liveHolder,
          selfAssessedPrice: live.selfAssessedPrice,
          deposit: live.balance,
          lastTaxTime: live.lastUpdateTime,
          lastPriceChangeTime: live.lastUpdateTime,
          heldSince: live.lastUpdateTime,
          isActive: live.isActive,
          createdAt: live.lastUpdateTime,
          createdTxHash: '',
        });
      }
    }

    // V2.2 lazy-seat synthesis: the indexer doesn't pre-materialise seats
    // (createGrid emits prices but no per-seat rows), and the server-side
    // route fills the gap with empty placeholder rows
    // (`holder=null, price=0, deposit=0`). Overlay creator-held synthetic
    // rows on those placeholders so the lobby grid renders the initial
    // price tag — without this every unclaimed tile reads "$0 / vacant"
    // instead of "creator listed @ <initial>".
    //
    // `synthesizeUnmaterializedSeats` only rewrites rows whose holder is
    // null AND price+deposit are 0 — any indexed/RPC-recovered row keeps
    // its authoritative values. V2.1 callers don't pass initialPrices so
    // the helper short-circuits.
    if (isV22GridVersion(grid?.gridVersion) && grid?.creator) {
      const initialPrices = parseInitialPricesPacked(grid.initialPricesPacked);
      if (initialPrices.length > 0) {
        return synthesizeUnmaterializedSeats(merged, {
          creator: grid.creator,
          initialPrices,
          gridCreatedAt: grid.createdAt ?? Math.floor(Date.now() / 1000),
        });
      }
    }

    return merged;
  }, [
    rawSeats,
    liveSeats,
    liveLoaded,
    gridId,
    grid?.gridVersion,
    grid?.creator,
    grid?.totalSeats,
    grid?.initialPricesPacked,
    grid?.createdAt,
  ]);

  const forfeitTimeBySeat = useMemo(
    () => buildForfeitTimeBySeat(recentEvents, mergedRawSeats),
    [recentEvents, mergedRawSeats],
  );

  const forfeitOldHolderBySeat = useMemo(
    () => buildForfeitOldHolderBySeat(recentEvents, mergedRawSeats),
    [recentEvents, mergedRawSeats],
  );

  const seats: SeatData[] = useMemo(
    () =>
      buildSeatData({
        rawSeats: mergedRawSeats,
        address,
        viewerAddresses,
        avatarUrl,
        displayName,
        userLevel,
        taxRateBps: grid?.taxRateBps,
        creator: grid?.creator,
        graduated: grid?.graduated,
        forfeitTimeBySeat,
        forfeitOldHolderBySeat,
        antiSnipeWindow: ANTI_SNIPE_WINDOW,
        // V22-only inputs: pass even on V21/V2 grids — `buildSeatData`
        // gates on `isV22Grid(managerAddress)` and falls back to the
        // existing `antiSnipeWindow` path when false. `forfeitureDuration`
        // is undefined for non-V22 grids (the API surfaces it from
        // `Grid.forfeitureDuration` only on V22 rows).
        managerAddress: grid?.managerAddress,
        forfeitureDuration: grid?.forfeitureDuration,
        reservations,
        claimDeadline,
      }),
    // `viewerAddressesKey` (sorted csv of `address` + every linked
    // wallet) is the stable dep — `viewerAddresses` is derived from it.
    // We keep `address` in deps too because `buildSeatData` still
    // compares it directly for `isOwn` (held tiles) and the
    // `forfeitPlinthIsViewer` / identity-fall-through paths, all of
    // which want the wagmi-active wallet specifically rather than the
    // broader linked-wallets set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedRawSeats, address, viewerAddressesKey, avatarUrl, displayName, userLevel, grid?.taxRateBps, grid?.creator, grid?.graduated, grid?.managerAddress, grid?.forfeitureDuration, forfeitTimeBySeat, forfeitOldHolderBySeat, ANTI_SNIPE_WINDOW, forfeitTick, reservations, claimDeadline],
  );

  // 1Hz client-side clock that re-runs `buildSeatData` so the displayed
  // runway visibly ticks down between API refreshes. Previously a
  // single-shot `setTimeout` only fired when the soonest runway was
  // ≤ 5 minutes, so any seat above that ceiling stayed frozen at the
  // last API value — which is exactly what the user saw as
  // "59 minutes left, no reduction anywhere".
  //
  // The tick runs while (a) any held seat still has positive runway or is
  // in pending-forfeit twilight, OR (b) any tile carries a live forfeit
  // window (`antiSnipeEndsAt` still in the future). Case (b) matters for
  // vacant V22 Dutch phases: without a 1 Hz recomputation, `now` inside
  // `buildSeatData` never advances after the last holder clears — users
  // would wait for SSE/poll for the instant the window ends and buying
  // unlocks.
  //
  // We deliberately don't invalidate `['v2-grid', gridId]` here — that
  // would kick the heavy grid endpoint once a second per viewer.
  // The cascade refetch on `liveEvents.length` (above) and the 15s
  // React Query interval are the only paths that touch the API; this
  // tick is purely client-side recomputation against the existing
  // `rpcCurrentBalanceAt` anchor.
  const needsSeatClock = useMemo(
    () =>
      seats.some((s) => {
        if (s.antiSnipeEndsAt != null) return true;
        if (!s.holder || s.isCreatorSeat || s.isForfeited) return false;
        return s.runwaySeconds > 0 || !!s.isPendingForfeit;
      }),
    [seats],
  );
  useEffect(() => {
    if (!needsSeatClock) return;
    const interval = setInterval(() => {
      setForfeitTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [needsSeatClock]);

  // Anti-snipe priority list: unique holders sorted by earliest heldSince (tenure).
  // Excludes the grid creator (creator seats are exempt from anti-snipe).
  const priorityHolders = useMemo(
    () => derivePriorityHolders(rawSeats, grid?.creator),
    [rawSeats, grid?.creator],
  );

  // Derive floor price from actual seat data when indexer hasn't caught up
  const { computedFloor, computedTakeover } = useMemo(
    () => computeFloorAndTakeover(seats),
    [seats],
  );

  const indexerFloor = grid ? safeBigInt(grid.floorPrice) : 0n;
  const indexerTakeover = grid ? safeBigInt(grid.takeoverCost) : 0n;
  const hasSeats = seats.length > 0;
  const floorPriceBigInt = hasSeats ? computedFloor : indexerFloor;
  const takeoverCostBigInt = hasSeats ? computedTakeover : indexerTakeover;

  /**
   * `getTaxTokenConfig(undefined, …)` defaults to 6dp — but live Envio seats often arrive
   * before the v2-grid REST payload, so `grid` is still undefined while
   * `selfAssessedPrice` is already on-chain 18‑dp TAKEOVER wei. Boardroom in particular
   * must not label those as USDC(6) or tile USD blows up until the grid record loads.
   */
  const taxTokenDecimals = useMemo(() => {
    const g = grid;
    if (g?.taxToken) {
      return getTaxTokenConfig(g.taxToken, undefined, g.taxTokenDecimals).decimals;
    }
    const boardroomId = getBoardroomLiveGridId();
    const isBoardroom = boardroomId != null && gridId === boardroomId;
    if (isBoardroom && TAKEOVER_TOKEN_ADDRESS) {
      return getTaxTokenConfig(
        TAKEOVER_TOKEN_ADDRESS as `0x${string}`,
        undefined,
        g?.taxTokenDecimals ?? 18,
      ).decimals;
    }
    return getTaxTokenConfig(undefined, undefined, g?.taxTokenDecimals).decimals;
  }, [grid, gridId]);

  const floorPriceNum = Number(floorPriceBigInt) / 10 ** taxTokenDecimals;
  const rawTakeoverCostNum = Number(takeoverCostBigInt) / 10 ** taxTokenDecimals;

  const isTakenOver = useMemo(() => {
    if (!address || !seats.length) return false;
    const activeSeats = seats.filter(s => s.holder);
    return activeSeats.length > 0 && activeSeats.every(s => s.isOwn);
  }, [address, seats]);

  const creatorOwnsAllTiles = useMemo(() => {
    if (!address || !grid?.totalSeats) return false;
    const total = grid.totalSeats;
    if (total <= 0 || seats.length === 0) return false;
    const byIndex = new Map(seats.map((s) => [s.seatIndex, s]));
    const v = address.toLowerCase();
    for (let i = 0; i < total; i++) {
      const s = byIndex.get(i);
      if (!s?.holder || s.isForfeited) return false;
      if (s.holder.toLowerCase() !== v) return false;
    }
    return true;
  }, [address, seats, grid?.totalSeats]);

  const isCreator = useMemo(() => {
    if (!address) return false;
    // Board-manager allow-list (`lib/admin/allow-list.ts`) — wallets
    // here see every grid's manage-mode UI as if they were the on-chain
    // creator. Server-side `requireGridCreator` enforces the same
    // override on the metadata write surface, so the affordance always
    // matches the actual write authority. On-chain creator-only actions
    // (`addYield`, `transferGridCreator`, etc.) still revert for these
    // wallets — that asymmetry is intentional. See the BOOTSTRAP_BOARD_MANAGERS
    // doc block for the full scope.
    if (isBoardManagerAddress(address)) return true;
    if (!grid?.creator) return false;
    return address.toLowerCase() === grid.creator.toLowerCase();
  }, [address, grid?.creator]);

  const hasVacantSeats = useMemo(() => {
    return seats.some(s => !s.holder);
  }, [seats]);

  const isFreeMint = hasVacantSeats;

  const ONE_WEEK_SECONDS = V2_CONSTANTS.SECONDS_PER_WEEK;

  // Compute trailing 7d rewards from fetched reward events (up to 500).
  const rewards7dRaw = useMemo(() => {
    const sevenDaysAgo = Math.floor(Date.now() / 1000) - ONE_WEEK_SECONDS;
    let sum = 0n;
    for (const e of rewardEvents) {
      if (e.timestamp >= sevenDaysAgo) sum += safeBigInt(e.amount);
    }
    return sum;
  }, [rewardEvents]);

  // Classify the yield token for reward calculations
  const rewardToken = useMemo(
    () => resolveRewardTokenInfo(yieldAddr, yieldDecimals, ethPrice, interactivePrices),
    [yieldAddr, yieldDecimals, ethPrice, interactivePrices],
  );

  const grossResult: RewardResult = useMemo(() => {
    if (!grid) return { amount: 0n, kind: rewardToken.kind, is7dBased: false, symbol: rewardToken.symbol, decimals: rewardToken.decimals };
    return grossRewardsPerTile({
      totalRewardsRaw: safeBigInt(grid.totalYieldReceived),
      rewards7dRaw,
      poolCreatedAt: grid.createdAt || 0,
      rewardToken,
      tileCount: grid.totalSeats || 100,
    });
  }, [grid, rewards7dRaw, rewardToken]);

  const grossPerSeat = grossResult.amount;
  const is7dEstimate = grossResult.is7dBased;

  const poolAgeLabel = useMemo(() => {
    if (!grid || is7dEstimate) return undefined;
    const createdAt = grid.createdAt || 0;
    if (!createdAt) return undefined;
    const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - createdAt);
    if (ageSec < 3600) return `${Math.max(1, Math.floor(ageSec / 60))}m old`;
    if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h old`;
    return `${Math.floor(ageSec / 86400)}d old`;
  }, [grid, is7dEstimate]);

  const isEarningsUnreliable = useMemo(() => {
    if (!grid || is7dEstimate) return false;
    const createdAt = grid.createdAt || 0;
    if (!createdAt) return false;
    const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - createdAt);
    return ageSec < 8 * 3600;
  }, [grid, is7dEstimate]);

  const totalDistributedUsd = useMemo(() => {
    if (!grid || !yieldPriceUsd) return 0n;
    const raw = safeBigInt(grid.totalYieldReceived);
    if (raw === 0n) return 0n;
    // `rawYieldToUsd` can return NaN/Infinity if its dependencies (price,
    // decimals) momentarily settle to weird values during a refetch;
    // `Math.floor(NaN)` is NaN and `BigInt(NaN)` throws.
    return safeBigInt(Math.floor(rawYieldToUsd(raw) * 1e6));
  }, [grid, yieldPriceUsd, rawYieldToUsd]);

  const totalDistributedRaw = useMemo(() => {
    if (!grid) return 0;
    const raw = safeBigInt(grid.totalYieldReceived);
    return Number(raw) / 10 ** yieldDecimals;
  }, [grid, yieldDecimals]);

  const floorPriceChange24h = useMemo(() => {
    if (!floorPriceSnapshots || floorPriceSnapshots.length < 2) return null;
    const now = Math.max(...floorPriceSnapshots.map(s => s.timestamp));
    const dayAgo = now - 86400;
    const oldSnap = floorPriceSnapshots.reduce((prev, curr) =>
      Math.abs(curr.timestamp - dayAgo) < Math.abs(prev.timestamp - dayAgo) ? curr : prev
    );
    const oldFloor = Number(oldSnap.floorPrice) / 10 ** taxTokenDecimals;
    if (oldFloor === 0) return null;
    return ((floorPriceNum - oldFloor) / oldFloor) * 100;
  }, [floorPriceSnapshots, floorPriceNum, taxTokenDecimals]);

  // Derive the non-yield token address (the "base" token users care about).
  // For a USDC/WETH LP where WETH is the yieldToken, this returns the USDC address.
  const primaryCoinAddress = useMemo(
    () => resolvePrimaryCoinAddress(assets, yieldAddr),
    [assets, yieldAddr],
  );

  // Derive the yield token symbol for display (e.g. "WETH", "USDC").
  // Try to match the yield address against the asset's token pair; fall back
  // to well-known Base addresses.
  const yieldTokenSymbol = useMemo(
    () => resolveYieldTokenSymbol(yieldAddr, assets),
    [yieldAddr, assets],
  );

  const hasYieldPrice = yieldPriceUsd > 0;

  // When USD price is available, convert reward amounts to USD-in-18-dec
  // so the RewardsTab's (amount/1e18)*1 formula yields correct USD.
  // When no price is available (exotic tokens), pass raw amounts through
  // scaled to human-readable units in 18-dec so the same division works.
  const rewardEventsUsd = useMemo(() => {
    if (hasYieldPrice) {
      return rewardEvents.map(r => {
        const decimals = r.adapterType === 'flaunch' ? 18 : yieldDecimals;
        const price = r.adapterType === 'flaunch' ? (ethPrice || yieldPriceUsd) : yieldPriceUsd;
        const usdValue = tokenToUsd(safeBigInt(r.amount), decimals, price);
        return { ...r, amount: (usdValue * 1e18).toFixed(0) };
      });
    }
    // No USD price — convert raw token amounts to human-readable * 1e18
    return rewardEvents.map(r => {
      const decimals = r.adapterType === 'flaunch' ? 18 : yieldDecimals;
      const human = Number(safeBigInt(r.amount)) / 10 ** decimals;
      return { ...r, amount: (human * 1e18).toFixed(0) };
    });
  }, [rewardEvents, hasYieldPrice, yieldPriceUsd, yieldDecimals, ethPrice]);

  // Pending adapter yield in the same display format as rewardEventsUsd amounts
  const pendingYieldDisplay = useMemo(() => {
    const raw = safeBigInt(gridData?.pendingAdapterYield);
    if (raw === 0n) return 0;
    if (hasYieldPrice) {
      return tokenToUsd(raw, yieldDecimals, yieldPriceUsd) * 1e18;
    }
    return (Number(raw) / 10 ** yieldDecimals) * 1e18;
  }, [gridData?.pendingAdapterYield, hasYieldPrice, yieldPriceUsd, yieldDecimals]);

  return {
    address,
    avatarUrl,
    displayName,
    userLevel,
    grid,
    gridLoading,
    gridError: gridError as Error | null,
    seats,
    rawSeats,
    assets,
    floorPriceSnapshots,
    recentEvents,
    rewardEvents,
    rewardEventsUsd,
    topHolders,
    creatorIdentity,
    floorPrice: floorPriceNum,
    floorPriceBigInt,
    takeoverCost: rawTakeoverCostNum.toFixed(2),
    rawTakeoverCost: rawTakeoverCostNum,
    takeoverCostBigInt,
    isTakenOver,
    creatorOwnsAllTiles,
    isCreator,
    hasVacantSeats,
    isFreeMint,
    grossPerSeat,
    grossResult,
    is7dEstimate,
    poolAgeLabel,
    isEarningsUnreliable,
    totalDistributedUsd,
    totalDistributedRaw,
    ethPrice: ethPrice ?? 0,
    floorPriceChange24h,
    priorityHolders,
    primaryCoinAddress,
    yieldTokenSymbol,
    yieldTokenAddress: yieldAddr,
    yieldTokenDecimals: yieldDecimals,
    hasYieldPrice,
    yieldPriceUsd,
    totalTvlUsd: grid?.totalTvlUsd,
    totalTvlNative: grid?.totalTvlNative,
    rawSocialLinks: rawSocialLinks,
    boardDescription: grid?.description,
    // Surfaced verbatim from `pool_metadata.board_skybox` /
    // `board_plinth` so consumers (`LobbyClient.activeBoard` overlay,
    // future per-grid surfaces) can apply the wizard's choice
    // without waiting on the lobby-list query.
    boardSkybox: grid?.boardSkybox,
    boardPlinth: grid?.boardPlinth,
    adapterNotes,
    taxTokenDecimals,
    pendingYieldDisplay,
    takeoverPriceUsdIndexer: gridData?.takeoverPriceUsdc
      ? Number(gridData.takeoverPriceUsdc) / 1e18
      : 0,
    yieldPriceWarning,
    migration,
  };
}

